import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTally } from '../hooks/useTally.jsx'
import { useTallyI18n } from '../i18n/tally.js'
import { formatAmount, formatDay } from '../format.js'
import { loadNames, renameNames, clearNames } from '../api.js'

function fmtDate(iso, locale, dateFormat) {
  if (!iso) return '—'
  return formatDay(iso, dateFormat, locale)
}

function fillTpl(s, vars) {
  return Object.entries(vars).reduce((acc, [k, v]) => acc.replaceAll(`{${k}}`, String(v)), s)
}

export function Manage() {
  const t = useTallyI18n()
  const navigate = useNavigate()
  const { data, clearTxCache } = useTally()

  // Mobile redirect — /manage is desktop-only
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)')
    const check = () => { if (mq.matches) navigate('/', { replace: true }) }
    check()
    mq.addEventListener?.('change', check)
    return () => mq.removeEventListener?.('change', check)
  }, [navigate])

  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [query, setQuery] = useState('')
  const [sortKey, setSortKey] = useState('n') // 'name' | 'n' | 'totalBase' | 'lastDate'
  const [sortDir, setSortDir] = useState('desc')
  const [selected, setSelected] = useState(() => new Set())
  const [editingName, setEditingName] = useState(null) // original name being edited
  const [draftName, setDraftName] = useState('')
  const [confirm, setConfirm] = useState(null)
  const [busy, setBusy] = useState(false)
  const [flash, setFlash] = useState(null) // success message

  async function refresh() {
    setLoading(true)
    try {
      const res = await loadNames()
      setItems(res.items ?? [])
      setError(null)
    } catch (e) {
      setError(e.message ?? String(e))
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { refresh() }, [])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const arr = q ? items.filter(i => i.name.toLowerCase().includes(q)) : items.slice()
    arr.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1
      if (sortKey === 'name')      return a.name.localeCompare(b.name) * dir
      if (sortKey === 'n')         return (a.n - b.n) * dir
      if (sortKey === 'totalBase') return (a.totalBase - b.totalBase) * dir
      if (sortKey === 'lastDate')  return (new Date(a.lastDate) - new Date(b.lastDate)) * dir
      return 0
    })
    return arr
  }, [items, query, sortKey, sortDir])

  function toggleSort(key) {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir(key === 'name' ? 'asc' : 'desc')
    }
  }

  function toggleSelect(name) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name); else next.add(name)
      return next
    })
  }
  function toggleSelectAll() {
    const visibleNames = filtered.map(i => i.name)
    const allSelected = visibleNames.every(n => selected.has(n))
    setSelected(prev => {
      const next = new Set(prev)
      if (allSelected) for (const n of visibleNames) next.delete(n)
      else             for (const n of visibleNames) next.add(n)
      return next
    })
  }

  function startEdit(name) {
    setEditingName(name)
    setDraftName(name)
  }
  function cancelEdit() {
    setEditingName(null)
    setDraftName('')
  }
  function commitEdit() {
    const from = editingName
    const to = draftName.trim()
    if (!to || to === from) { cancelEdit(); return }
    const item = items.find(i => i.name === from)
    setConfirm({ kind: 'rename', from, to, n: item?.n ?? 0 })
  }
  function openMerge() {
    if (selected.size < 1) return
    const sources = [...selected]
    const target = window.prompt(
      fillTpl(t.manage.mergePrompt, { n: selected.size }),
      sources[0],
    )
    if (!target) return
    const to = target.trim()
    if (!to) return
    const totalN = sources.reduce((s, name) => s + (items.find(i => i.name === name)?.n ?? 0), 0)
    setConfirm({ kind: 'merge', from: sources, to, n: totalN })
  }
  function openClear() {
    if (selected.size < 1) return
    const sources = [...selected]
    const totalN = sources.reduce((s, name) => s + (items.find(i => i.name === name)?.n ?? 0), 0)
    setConfirm({ kind: 'clear', names: sources, n: totalN })
  }

  async function applyConfirm() {
    if (!confirm || busy) return
    setBusy(true)
    try {
      if (confirm.kind === 'rename') {
        const res = await renameNames({ from: confirm.from, to: confirm.to })
        setFlash(fillTpl(t.manage.doneRenamed, { n: res.modified ?? 0 }))
      } else if (confirm.kind === 'merge') {
        const res = await renameNames({ from: confirm.from, to: confirm.to })
        setFlash(fillTpl(t.manage.doneRenamed, { n: res.modified ?? 0 }))
        setSelected(new Set())
      } else if (confirm.kind === 'clear') {
        const res = await clearNames(confirm.names)
        setFlash(fillTpl(t.manage.doneCleared, { n: res.modified ?? 0 }))
        setSelected(new Set())
      }
      cancelEdit()
      setConfirm(null)
      clearTxCache()
      await refresh()
      setTimeout(() => setFlash(null), 4000)
    } catch (e) {
      setError(e.message ?? String(e))
    } finally {
      setBusy(false)
    }
  }

  const cur = data.currency ?? 'EUR'
  const visibleAllSelected = filtered.length > 0 && filtered.every(i => selected.has(i.name))

  return (
    <div className="view view-manage">
      <header className="manage-head">
        <div>
          <h1 className="manage-title">{t.manage.title}</h1>
          <p className="manage-sub">{t.manage.sub}</p>
        </div>
        <input
          className="manage-search"
          type="search"
          placeholder={t.manage.search}
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
      </header>

      {selected.size > 0 && (
        <div className="manage-bulk">
          <span className="manage-bulk-count">
            {fillTpl(t.manage.selectedN, { n: selected.size })}
          </span>
          <button type="button" className="btn btn-primary" onClick={openMerge}>
            {t.manage.mergeInto}
          </button>
          <button type="button" className="btn btn-danger-outline" onClick={openClear}>
            {t.manage.clearName}
          </button>
          <button type="button" className="btn" onClick={() => setSelected(new Set())}>
            {t.manage.cancel}
          </button>
        </div>
      )}

      {flash && <div className="manage-flash">{flash}</div>}
      {error && <div className="manage-error">{error}</div>}

      {loading ? (
        <div className="view-empty"><div className="loading-spinner" /></div>
      ) : items.length === 0 ? (
        <div className="view-empty"><span>{t.manage.empty}</span></div>
      ) : filtered.length === 0 ? (
        <div className="view-empty"><span>{t.manage.noResults}</span></div>
      ) : (
        <div className="manage-table-wrap">
          <table className="manage-table">
            <thead>
              <tr>
                <th className="col-select">
                  <input
                    type="checkbox"
                    checked={visibleAllSelected}
                    onChange={toggleSelectAll}
                    aria-label="Select all"
                  />
                </th>
                <th className="col-name sortable" onClick={() => toggleSort('name')}>
                  {t.manage.colName}{sortKey === 'name' && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                </th>
                <th className="col-count sortable" onClick={() => toggleSort('n')}>
                  {t.manage.colCount}{sortKey === 'n' && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                </th>
                <th className="col-total sortable" onClick={() => toggleSort('totalBase')}>
                  {t.manage.colTotal}{sortKey === 'totalBase' && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                </th>
                <th className="col-last sortable" onClick={() => toggleSort('lastDate')}>
                  {t.manage.colLast}{sortKey === 'lastDate' && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(it => {
                const isEditing = editingName === it.name
                const isSelected = selected.has(it.name)
                return (
                  <tr key={it.name} className={isSelected ? 'is-selected' : undefined}>
                    <td className="col-select">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(it.name)}
                      />
                    </td>
                    <td className="col-name">
                      {isEditing ? (
                        <EditCell
                          value={draftName}
                          onChange={setDraftName}
                          onCommit={commitEdit}
                          onCancel={cancelEdit}
                        />
                      ) : (
                        <button
                          type="button"
                          className="manage-name-cell"
                          onClick={() => startEdit(it.name)}
                          title="Click to edit"
                        >
                          {it.name}
                        </button>
                      )}
                    </td>
                    <td className="col-count">{it.n}</td>
                    <td className="col-total">{formatAmount(it.totalBase, cur)}</td>
                    <td className="col-last">{fmtDate(it.lastDate, undefined, data.dateFormat)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {confirm && (
        <ConfirmModal
          confirm={confirm}
          busy={busy}
          t={t}
          onApply={applyConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  )
}

function EditCell({ value, onChange, onCommit, onCancel }) {
  const ref = useRef(null)
  useEffect(() => { ref.current?.focus(); ref.current?.select() }, [])
  return (
    <div className="manage-edit-cell">
      <input
        ref={ref}
        className="manage-name-input"
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter')  { e.preventDefault(); onCommit() }
          if (e.key === 'Escape') { e.preventDefault(); onCancel() }
        }}
      />
      <button type="button" className="btn-icon" onClick={onCommit} aria-label="OK">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
      </button>
      <button type="button" className="btn-icon" onClick={onCancel} aria-label="Cancel">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>
  )
}

function ConfirmModal({ confirm, busy, t, onApply, onCancel }) {
  let title = '', sub = ''
  if (confirm.kind === 'rename') {
    title = fillTpl(t.manage.confirmRename, { from: confirm.from, to: confirm.to })
    sub   = fillTpl(t.manage.confirmRenameSub, { n: confirm.n })
  } else if (confirm.kind === 'merge') {
    title = fillTpl(t.manage.confirmMerge, { fromCount: confirm.from.length, to: confirm.to })
    sub   = fillTpl(t.manage.confirmMergeSub, { n: confirm.n, list: confirm.from.join(', ') })
  } else if (confirm.kind === 'clear') {
    title = fillTpl(t.manage.confirmClear, { n: confirm.n })
    sub   = fillTpl(t.manage.confirmClearSub, { list: confirm.names.join(', ') })
  }
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal manage-confirm" onClick={e => e.stopPropagation()}>
        <h3 className="modal-title">{title}</h3>
        <p className="modal-sub">{sub}</p>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            {t.manage.cancel}
          </button>
          <button
            type="button"
            className={`btn ${confirm.kind === 'clear' ? 'btn-danger' : 'btn-primary'}`}
            onClick={onApply}
            disabled={busy}
          >
            {t.manage.apply}
          </button>
        </div>
      </div>
    </div>
  )
}
